import * as React from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/utils/cn'

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange'
> {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  indeterminate?: boolean
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked, onCheckedChange, indeterminate, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement>(null)
    const combinedRef = (ref as React.RefObject<HTMLInputElement>) || innerRef

    React.useEffect(() => {
      if (combinedRef.current) {
        combinedRef.current.indeterminate = indeterminate ?? false
      }
    }, [indeterminate, combinedRef])

    return (
      <label className="relative inline-flex items-center">
        <input
          type="checkbox"
          ref={combinedRef}
          checked={checked}
          onChange={e => onCheckedChange?.(e.target.checked)}
          className="sr-only peer"
          {...props}
        />
        <div
          className={cn(
            'h-4 w-4 shrink-0 rounded border border-primary ring-offset-background',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'peer-checked:bg-primary peer-checked:text-primary-foreground',
            'flex items-center justify-center cursor-pointer',
            indeterminate && 'bg-primary text-primary-foreground',
            className
          )}
        >
          {checked && !indeterminate && <Check className="h-3 w-3" />}
          {indeterminate && <Minus className="h-3 w-3" />}
        </div>
      </label>
    )
  }
)
Checkbox.displayName = 'Checkbox'

export { Checkbox }
